import { useEffect, useMemo } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  Stack,
  useDisclosure
} from "@chakra-ui/react";
import { Form, Select, Tooltip } from "antd";
import { BiSlider } from "react-icons/bi";
import styled from "styled-components";
import { FiltrosAvancado } from "../../../interfaces/Planejamento";
import { useFilters } from "core/features/filters/store";
import { useSetorList, useTalhoesList } from "../../../hooks/useRelatorio";
import useRelatorioContext from "../../../hooks/useRelatorioContext";
import { SELECT_POPUP_Z_INDEX } from "../utils";
import { useValidatePermission } from "core/features/auth/hooks/useValidatePermission";

type Props = {
  filtersData?: FiltrosAvancado;
}

const { Item, useForm } = Form;

const RelatorioPopoverContent = PopoverContent as any;
const RelatorioForm = Form as any;
const RelatorioPopoverHeader = PopoverHeader as any;
const RelatorioPopoverBody = PopoverBody as any;
const RelatorioPopoverFooter = PopoverFooter as any;
const RelatorioStack = Stack as any;
const RelatorioButton = Button as any;

export function FiltrosAvancados({ filtersData }: Props) {
  const [form] = useForm();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { filters } = useFilters();
  const expandScreen = useRelatorioContext((s) => s.expandScreen);
  const setQueryParams = useRelatorioContext((s) => s.setQueryParams);
  const { filtersAdvanced, unidade, safra } = filters;
  const { hasPermission } = useValidatePermission();
  const permissionProgramacaoOperacional = hasPermission(['Relatorios.CronogramaOperacional']);
  const permissionAnaliticoOperacional = hasPermission(['Relatorios.RelatorioAnaliticoOperacional']);

  const { data: listSetores } = useSetorList({
    UnidadeId: unidade?.id,
    SafraId: safra?.id,
  });

  const { data: listTalhoes } = useTalhoesList({
    UnidadeId: unidade?.id,
    SafraPeriodoId: filtersAdvanced?.safraPeriodo,
  });

  const activeFiltersCount = [
    !!filtersAdvanced?.operacao,
  ].filter(Boolean).length;

  const talhaoSelectOptions = useMemo(() => {
    if (!listTalhoes?.length) return [];
    return [...listTalhoes]
      .map((item: any) => ({
        label: item.text,
        value: item.value,
      }));
  }, [listTalhoes]);

  function handleSearch() {
    const body = form.getFieldsValue();
    onClose();
    queueMicrotask(() => {
      setQueryParams(body);
    });
  }

  function handleClear() {
    form.resetFields();
  }

  useEffect(() => {
    if (!filtersAdvanced?.cultura) {
      form.resetFields();
    }
  }, [!filtersAdvanced?.cultura]);

  return (
    <Popover placement="bottom-start" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <ButtonSliderWrapper disabled={!permissionProgramacaoOperacional || !permissionAnaliticoOperacional}>
          {activeFiltersCount > 0 && <IconCircle>{activeFiltersCount}</IconCircle>}
          <BiSlider />
        </ButtonSliderWrapper>
      </PopoverTrigger>
      <RelatorioPopoverContent p={4} w="sm">
        <PopoverArrow />
        <PopoverCloseButton />
        <RelatorioPopoverHeader fontWeight="bold">Filtros Avançados</RelatorioPopoverHeader>
        <RelatorioForm form={form} layout="vertical">
          <RelatorioPopoverBody>
            <RelatorioStack spacing={2}>
              <Wrap>
                <Item label="Classe de Operação" name="OperacaoClasseIds">
                  <SelectAntd
                    placeholder="Classe de Operação"
                    allowClear
                    showSearch
                    mode="multiple"
                    maxTagCount="responsive"
                    defaultValue={filtersAdvanced?.classeOperacao}
                    optionFilterProp="label"
                    options={(filtersData?.operacaoClasses || [])
                      ?.map((item: any) => ({
                        label: item.descricao,
                        value: item.operacaoClasseId,
                      }))}
                    maxTagPlaceholder={(omittedValues) => (
                      <Tooltip
                        overlayStyle={
                          expandScreen ? { zIndex: SELECT_POPUP_Z_INDEX } : undefined
                        }
                        title={omittedValues.map((v) => v.label).join(", ")}
                        placement="top"
                      >
                        +{omittedValues.length} itens
                      </Tooltip>
                    )}
                  />
                </Item>
              </Wrap>
            
              <Wrap>
                <Item label="Operação" name="OperacaoIds">
                  <SelectAntd
                    placeholder="Operação"
                    allowClear
                    showSearch
                    mode="multiple"
                    maxTagCount="responsive"
                    optionFilterProp="label"
                    options={(filtersData?.operacoes || [])
                      ?.map((item: any) => ({
                        label: item.descricao,
                        value: item.operacaoId,
                      }))}
                    maxTagPlaceholder={(omittedValues) => (
                      <Tooltip
                        overlayStyle={
                          expandScreen ? { zIndex: SELECT_POPUP_Z_INDEX } : undefined
                        }
                        title={omittedValues.map((v) => v.label).join(", ")}
                        placement="top"
                      >
                        +{omittedValues.length} itens
                      </Tooltip>
                    )}
                  />
                </Item>
              </Wrap>
              <Wrap>
                <Item label="Setor" name="SetorIds">
                  <SelectAntd
                    // {...selectPopupProps}
                    placeholder="Setor"
                    allowClear
                    showSearch
                    mode="multiple"
                    maxTagCount="responsive"
                    optionFilterProp="label"
                    options={(listSetores || [])
                      ?.map((item: any) => ({
                        label: item.nome,
                        value: item.id,
                      }))}
                    maxTagPlaceholder={(omittedValues) => (
                      <Tooltip
                        overlayStyle={
                          expandScreen ? { zIndex: SELECT_POPUP_Z_INDEX } : undefined
                        }
                        title={omittedValues.map((v) => v.label).join(", ")}
                        placement="top"
                      >
                        +{omittedValues.length} itens
                      </Tooltip>
                    )}
                  />
                </Item>
              </Wrap>
              <Wrap>
                <Item label="Talhão" name="TalhaoIds">
                  <SelectAntd
                    // {...selectPopupProps}
                    placeholder="Talhão"
                    allowClear
                    showSearch
                    mode="multiple"
                    maxTagCount="responsive"
                    optionFilterProp="label"
                    options={talhaoSelectOptions}
                    maxTagPlaceholder={(omittedValues) => (
                      <Tooltip
                        overlayStyle={
                          expandScreen ? { zIndex: SELECT_POPUP_Z_INDEX } : undefined
                        }
                        title={omittedValues.map((v) => v.label).join(", ")}
                        placement="top"
                      >
                        +{omittedValues.length} itens
                      </Tooltip>
                    )}
                  />
                </Item>
              </Wrap>

            </RelatorioStack>
          </RelatorioPopoverBody>

          <RelatorioPopoverFooter display="flex" justifyContent="space-between">
            <RelatorioButton variant="ghost" onClick={handleClear}>Limpar</RelatorioButton>
            <RelatorioButton colorScheme="green" onClick={handleSearch}>Aplicar</RelatorioButton>
          </RelatorioPopoverFooter>
        </RelatorioForm>
      </RelatorioPopoverContent>
    </Popover>
  );
}

const ButtonSliderWrapper = styled.button`
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    min-width: 45px;
    height: 60px;

    svg {
      font-size: 20px;
    }

    :active {
        background: #20a45a;
        border-color: #20a45a;
    }

    :disabled {
      opacity: 0.5;
      cursor: not-allowed;

    }
`;

const Wrap = styled.div`
    width: 100%;

    .ant-form-item {
      margin-bottom: 0;
    }

    .ant-col {
      padding: 0;
    }
`;

const SelectAntd = styled(Select)`
  width: 100% !important;
  
  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    padding: 4px 11px !important;
  }

  .ant-form-item-label {
    text-align: left !important;
  }

   &.ant-select-multiple > .ant-select-selector {
    border: 1px solid #d9d9d9 !important;
     box-shadow: none !important;
     outline: none !important;
  }
  &.ant-select-multiple > .ant-select-selector:hover {
    border: 1px solid #40a9ff !important;
     box-shadow: none !important;
     outline: none !important;
  }
  &.ant-select-multiple.ant-select-open > .ant-select-selector {
    border-color: #40a9ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
    border-right-width: 1px !important;
    outline: 0;
  }
`;

const IconCircle = styled.span`
  width: 16px;
  height: 16px;
  position: absolute;
  top: 2px;
  right: 1px;
  border-radius: 50%;
  background-color: #ff0000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;
